import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PRIORITIES } from "@/lib/constants";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const field = "rounded-xl bg-[#FDFBF7] border-[#103080]/20 h-11";
const PRESETS = [30, 60, 90, 120, 180, 240, 360];

export default function ScheduleCardDialog({ card, assigneeName, isMine, onClose, onSaved }) {
  const [f, setF] = useState({ duration_minutes: 60, start_date: "", priority: "medium" });

  useEffect(() => {
    if (card) setF({ duration_minutes: card.duration_minutes, start_date: card.start_date || "", priority: card.priority });
  }, [card]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/tasks/${card.id}`, { in_planner: true, duration_minutes: Number(f.duration_minutes), start_date: f.start_date || null, priority: f.priority });
      toast.success(isMine ? "Added to your schedule" : `Added to ${assigneeName}'s schedule`);
      onClose();
      onSaved(card);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={Boolean(card)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 sm:max-w-md" data-testid="schedule-card-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-[#103080]">Add to schedule</DialogTitle>
          <DialogDescription>{card?.client_name} · {card?.title} · {card?.period_label}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label>How long will this take? (minutes)</Label>
            <div className="flex flex-wrap gap-2">
              <Input type="number" min={5} step={5} required value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: e.target.value })}
                className={`${field} w-28`} data-testid="schedule-card-duration-input" />
              {PRESETS.map((m) => (
                <button type="button" key={m} onClick={() => setF({ ...f, duration_minutes: m })} data-testid={`schedule-card-preset-${m}`}
                  className={`rounded-full px-3 h-11 text-xs font-semibold border transition-colors duration-200 ${Number(f.duration_minutes) === m ? "bg-[#103080] text-white border-[#103080]" : "border-[#103080]/20 text-[#103080] hover:border-[#0090D0]"}`}>
                  {m < 60 ? `${m}m` : `${m / 60}h`}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Don't start before</Label>
              <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={field} data-testid="schedule-card-start-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger className={field} data-testid="schedule-card-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {!isMine && <p className="text-xs text-[#0090D0]">This goes into {assigneeName}'s planner.</p>}
          <div className="flex justify-end pt-2">
            <button type="submit" data-testid="schedule-card-save-button"
              className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200">
              Add to schedule
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
